import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useVisibleTabs } from '@/features/session/use-session';
import { cn } from '@/lib/cn';

/** As três vistas do Cronograma, na ordem pedida em 19/10/2026: primeiro a mão de obra. */
export const CRONOGRAMA_VISTAS = [
  { to: '/cronograma/itens', label: 'Mão de obra' },
  { to: '/cronograma', label: 'Materiais' },
  { to: '/cronograma/execucao', label: 'Execução por cômodo' },
] as const;

export type CronogramaVista = (typeof CRONOGRAMA_VISTAS)[number]['to'];

/**
 * O cabeçalho das três vistas do Cronograma (redesenho de 21/10/2026).
 *
 * Um título só, "Cronograma", com as vistas como abas embaixo: mão de obra, materiais e
 * execução por cômodo são três leituras do mesmo cronograma, e não três telas. As abas são
 * links (cada vista tem a sua rota), e por isso `nav` + `aria-current`, e não `role="tab"`.
 *
 * À direita, as ações da vista: um botão primário e, quando há mais, o menu "Mais". A
 * navegação saiu da fileira de botões — ela já está no menu lateral e nas abas.
 */
export function CronogramaHeader({
  atual,
  actions,
}: {
  readonly atual: CronogramaVista;
  readonly actions?: ReactNode;
}) {
  // Só as vistas marcadas no papel (RN-626); a do endereço que ele não vê, a casca já trocou.
  const doPapel = useVisibleTabs('/cronograma');
  const vistas = CRONOGRAMA_VISTAS.filter((vista) => doPapel.some((tab) => tab.path === vista.to));
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="flex min-w-0 flex-col gap-3">
        <h1 className="text-[1.75rem] leading-none font-semibold tracking-tight">Cronograma</h1>
        <nav
          aria-label="Vistas do cronograma"
          className="-mb-px flex flex-wrap gap-1 border-b border-(--color-border-subtle)"
        >
          {vistas.map((vista, index) => {
            const selecionada = vista.to === atual;
            return (
              <Link
                key={vista.to}
                to={vista.to}
                aria-current={selecionada ? 'page' : undefined}
                className={cn(
                  '-mb-px shrink-0 border-b-2 py-2.5 text-[15px] whitespace-nowrap',
                  index === 0 ? 'pr-3.5 pl-0.5' : 'px-3.5',
                  selecionada
                    ? 'border-(--color-brand) font-semibold text-(--color-content)'
                    : 'border-transparent font-medium text-(--color-content-muted) hover:text-(--color-content)',
                )}
              >
                {vista.label}
              </Link>
            );
          })}
        </nav>
      </div>
      {actions !== undefined && (
        <div className="flex flex-wrap items-center gap-2 pb-1">{actions}</div>
      )}
    </header>
  );
}
